import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';
import './MccForm.css';

const BRANCH_OFFICES = [
  'Latur Branch',
  'Pune Branch',
  'Kolhapur Branch',
  'Nanded Branch',
  'Solapur Branch'
];

const QUOTA_OPTIONS = [
  'AFMC',
  '15% All India Quota Govt.',
  'AIIMS/JIPMER',
  'Deemed Universities',
  'Central Universities (Delhi University, AMU & BHU)'
];

const RELIGIONS = [
  'Hinduism',
  'Islam',
  'Sikhism',
  'Jain',
  'Buddhism',
  'Other'
];

const CATEGORIES = [
  'OPEN',
  'OBC (VJ/NT/SBC)',
  'EWS',
  'SC',
  'ST'
];

const HSC_BOARDS = [
  'Maharashtra State Board',
  'CBSE',
  'ICSE',
  'OTHER'
];

const HSC_YEARS = Array.from({ length: 27 }, (_, i) => String(2026 - i));

const STATES = [
  'Maharashtra',
  'Telangana',
  'Andhra Pradesh',
  'Karnataka',
  'Other'
];

const initialFormData = {
  formType: 'mcc-quota',
  registeredWith: '',
  appliedFor: [],
  otherAppliedFor: '',
  studentName: '',
  neetRollNumber: '',
  neetApplicationNumber: '',
  mothersName: '',
  dateOfBirth: '',
  gender: '',
  mobileNumber: '',
  alternateMobile: '',
  religion: '',
  reservationCategory: '',
  pwd: '',
  hscBoard: '',
  hscPassingYear: '',
  hscSeatNumber: '',
  hscCollegeName: '',
  hscCollegePincode: '',
  hscPhysicsMarks: '',
  hscChemistryMarks: '',
  hscBiologyMarks: '',
  address: '',
  taluka: '',
  district: '',
  state: '',
  pinCode: '',
  accountHolderName: '',
  accountNumber: '',
  ifscCode: '',
  bankName: '',
  branchAddress: ''
};

// Apps Script Web App URL from setup guide
const APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbx8oEnZ5-b7NOZsCDeZOj0YM16EUWjQMD8CS-X09ip2Uqr-sNR_FlBRNwSkuWzLd6aA/exec";

export default function MccForm({ onBack }) {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const pdfRef = React.useRef(null);

  // Helper lists for step navigation
  const steps = [
    { id: 'sec1', title: 'Registration & Personal Details' },
    { id: 'sec2', title: 'Qualification Details' },
    { id: 'sec3', title: 'Address Details (Aadhaar)' },
    { id: 'sec4', title: 'Refund Bank Details' },
    { id: 'sec5', title: 'Review & Summary' }
  ];

  const currentStep = steps[stepIndex];

  // ─── VALIDATION ────────────────────────────────────────
  const validateStep = () => {
    const newErrors = {};
    const nameRegex = /^[A-Za-z\s]+$/;
    const phoneRegex = /^[6-9]\d{9}$/;
    const numericRegex = /^\d+$/;

    if (stepIndex === 0) {
      if (!formData.registeredWith) newErrors.registeredWith = "Branch office is required";
      if (!formData.appliedFor.length && !formData.otherAppliedFor.trim()) {
        newErrors.appliedFor = "Select at least one quota option";
      }
      
      // Full Name Validation
      if (!formData.studentName.trim()) {
        newErrors.studentName = "Full Name is required";
      } else if (!nameRegex.test(formData.studentName)) {
        newErrors.studentName = "Full name must only contain letters and spaces";
      } else if (formData.studentName.trim().length < 3) {
        newErrors.studentName = "Full name must be at least 3 characters long";
      }

      // Mother's Name Validation
      if (!formData.mothersName.trim()) {
        newErrors.mothersName = "Mother's Name is required";
      } else if (!nameRegex.test(formData.mothersName)) {
        newErrors.mothersName = "Mother's name must only contain letters and spaces";
      } else if (formData.mothersName.trim().length < 2) {
        newErrors.mothersName = "Mother's name must be at least 2 characters long";
      }

      // NEET Roll Number Validation
      if (!formData.neetRollNumber.trim()) {
        newErrors.neetRollNumber = "NEET Roll Number is required";
      } else if (!numericRegex.test(formData.neetRollNumber)) {
        newErrors.neetRollNumber = "NEET Roll Number must contain digits only";
      } else if (formData.neetRollNumber.trim().length !== 10) {
        newErrors.neetRollNumber = "NEET Roll Number must be exactly 10 digits";
      }

      // NEET Application Number Validation
      if (!formData.neetApplicationNumber.trim()) {
        newErrors.neetApplicationNumber = "NEET Application Number is required";
      } else if (!numericRegex.test(formData.neetApplicationNumber)) {
        newErrors.neetApplicationNumber = "NEET Application Number must contain digits only";
      } else if (formData.neetApplicationNumber.trim().length !== 12) {
        newErrors.neetApplicationNumber = "NEET Application Number must be exactly 12 digits";
      }

      // Age/DOB Validation (Candidates must be born on or before December 31, 2009)
      if (!formData.dateOfBirth) {
        newErrors.dateOfBirth = "Date of Birth is required";
      } else {
        const dob = new Date(formData.dateOfBirth);
        const limitDate = new Date('2009-12-31');
        if (dob > limitDate) {
          newErrors.dateOfBirth = "Candidate must be born on or before 31-Dec-2009 (min 17 years old by end of admission year)";
        }
      }

      if (!formData.gender) newErrors.gender = "Gender is required";

      // Mobile & Alternate Mobile Validation
      if (!formData.mobileNumber.trim()) {
        newErrors.mobileNumber = "Mobile Number is required";
      } else if (!phoneRegex.test(formData.mobileNumber)) {
        newErrors.mobileNumber = "Invalid 10-digit mobile number starting with 6-9";
      }

      if (formData.alternateMobile.trim()) {
        if (!phoneRegex.test(formData.alternateMobile)) {
          newErrors.alternateMobile = "Invalid 10-digit alternate mobile number starting with 6-9";
        } else if (formData.mobileNumber === formData.alternateMobile) {
          newErrors.alternateMobile = "Alternate mobile number cannot be the same as primary mobile number";
        }
      }

      if (!formData.religion) newErrors.religion = "Religion is required";
      if (!formData.reservationCategory) newErrors.reservationCategory = "Category is required";
      if (!formData.pwd) newErrors.pwd = "PwD status is required";
    }

    if (stepIndex === 1) {
      if (!formData.hscBoard) newErrors.hscBoard = "12th Board Name is required";
      if (!formData.hscPassingYear) newErrors.hscPassingYear = "Passing Year is required";
      
      // HSC Seat Number Validation
      if (!formData.hscSeatNumber.trim()) {
        newErrors.hscSeatNumber = "12th Seat/Roll number is required";
      } else if (formData.hscSeatNumber.trim().length < 5 || formData.hscSeatNumber.trim().length > 12) {
        newErrors.hscSeatNumber = "12th Seat/Roll number must be between 5 and 12 characters";
      } else if (!/^[A-Z0-9]+$/i.test(formData.hscSeatNumber)) {
        newErrors.hscSeatNumber = "Seat number must be alphanumeric (no spaces or symbols)";
      }

      if (!formData.hscCollegeName.trim()) newErrors.hscCollegeName = "College Name & Address is required";
      
      if (!formData.hscCollegePincode.trim()) {
        newErrors.hscCollegePincode = "College Pincode is required";
      } else if (!/^\d{6}$/.test(formData.hscCollegePincode)) {
        newErrors.hscCollegePincode = "Pincode must be exactly 6 digits";
      }

      // HSC Obtained Marks Validation (Physics, Chemistry, Biology)
      const pMark = parseInt(formData.hscPhysicsMarks);
      const cMark = parseInt(formData.hscChemistryMarks);
      const bMark = parseInt(formData.hscBiologyMarks);

      if (!formData.hscPhysicsMarks.trim()) {
        newErrors.hscPhysicsMarks = "Physics marks required";
      } else if (isNaN(pMark) || pMark < 0 || pMark > 100) {
        newErrors.hscPhysicsMarks = "Marks must be between 0 and 100";
      }

      if (!formData.hscChemistryMarks.trim()) {
        newErrors.hscChemistryMarks = "Chemistry marks required";
      } else if (isNaN(cMark) || cMark < 0 || cMark > 100) {
        newErrors.hscChemistryMarks = "Marks must be between 0 and 100";
      }

      if (!formData.hscBiologyMarks.trim()) {
        newErrors.hscBiologyMarks = "Biology marks required";
      } else if (isNaN(bMark) || bMark < 0 || bMark > 100) {
        newErrors.hscBiologyMarks = "Marks must be between 0 and 100";
      }

      // PCB Aggregate Eligibility check
      if (!isNaN(pMark) && !isNaN(cMark) && !isNaN(bMark) && pMark >= 0 && pMark <= 100 && cMark >= 0 && cMark <= 100 && bMark >= 0 && bMark <= 100) {
        const pcbSum = pMark + cMark + bMark;
        const isGeneral = formData.reservationCategory === 'OPEN' || formData.reservationCategory === 'EWS';
        const requiredMin = isGeneral ? 150 : 120; // 50% for Gen/EWS, 40% for OBC/SC/ST
        
        if (pcbSum < requiredMin) {
          const categoryName = isGeneral ? "OPEN/EWS" : formData.reservationCategory;
          newErrors.hscBiologyMarks = `PCB Aggregate of ${pcbSum}/300 is below the minimum required ${requiredMin}/300 (50% for OPEN/EWS, 40% for OBC/SC/ST) for eligibility`;
        }
      }
    }

    if (stepIndex === 2) {
      if (!formData.address.trim()) {
        newErrors.address = "Address is required";
      } else if (formData.address.trim().length < 15) {
        newErrors.address = "Please enter a valid complete address (at least 15 characters)";
      }

      if (!formData.taluka.trim()) {
        newErrors.taluka = "Taluka is required";
      } else if (!nameRegex.test(formData.taluka)) {
        newErrors.taluka = "Taluka name must contain letters only";
      }

      if (!formData.district.trim()) {
        newErrors.district = "District is required";
      } else if (!nameRegex.test(formData.district)) {
        newErrors.district = "District name must contain letters only";
      }

      if (!formData.state) newErrors.state = "State is required";

      if (!formData.pinCode.trim()) {
        newErrors.pinCode = "Pincode is required";
      } else if (!/^\d{6}$/.test(formData.pinCode)) {
        newErrors.pinCode = "Pincode must be exactly 6 digits";
      }
    }

    if (stepIndex === 3) {
      // Account Holder Name Validation
      if (!formData.accountHolderName.trim()) {
        newErrors.accountHolderName = "Account Holder Name is required";
      } else if (!nameRegex.test(formData.accountHolderName)) {
        newErrors.accountHolderName = "Name must contain letters only";
      }

      // Account Number Validation
      if (!formData.accountNumber.trim()) {
        newErrors.accountNumber = "Account Number is required";
      } else if (!/^\d{9,18}$/.test(formData.accountNumber)) {
        newErrors.accountNumber = "Account Number must be between 9 and 18 digits";
      }

      // IFSC Code Validation
      const ifsc = formData.ifscCode.trim().toUpperCase();
      if (!formData.ifscCode.trim()) {
        newErrors.ifscCode = "IFSC Code is required";
      } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
        newErrors.ifscCode = "Invalid IFSC Code format (e.g. SBIN0001234)";
      }

      // Bank Name Validation
      if (!formData.bankName.trim()) {
        newErrors.bankName = "Bank Name is required";
      } else if (!nameRegex.test(formData.bankName)) {
        newErrors.bankName = "Bank Name must contain letters only";
      }

      if (!formData.branchAddress.trim()) newErrors.branchAddress = "Branch Address is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── HANDLERS ──────────────────────────────────────────
  const handleChange = (e) => {
    let { name, value } = e.target;
    
    // Real-time constraints
    if (['mobileNumber', 'alternateMobile', 'neetRollNumber'].includes(name)) {
      if (value !== '' && (!/^\d+$/.test(value) || value.length > 10)) return;
    }
    if (name === 'neetApplicationNumber') {
      if (value !== '' && (!/^\d+$/.test(value) || value.length > 12)) return;
    }
    if (['pinCode', 'hscCollegePincode'].includes(name)) {
      if (value !== '' && (!/^\d+$/.test(value) || value.length > 6)) return;
    }
    if (name === 'accountNumber') {
      if (value !== '' && (!/^\d+$/.test(value) || value.length > 18)) return;
    }
    if (['hscPhysicsMarks', 'hscChemistryMarks', 'hscBiologyMarks'].includes(name)) {
      if (value !== '' && (!/^\d+$/.test(value) || value.length > 3)) return;
    }
    if (name === 'hscSeatNumber') {
      value = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
    }
    if (name === 'ifscCode') {
      value = value.toUpperCase().slice(0, 11);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleCheckboxChange = (option) => {
    setFormData(prev => {
      const selected = prev.appliedFor.includes(option)
        ? prev.appliedFor.filter(o => o !== option)
        : [...prev.appliedFor, option];
      return { ...prev, appliedFor: selected };
    });
    if (errors.appliedFor) {
      setErrors(prev => ({ ...prev, appliedFor: null }));
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setStepIndex(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      toast.error("Please resolve all errors before proceeding.");
    }
  };

  const handlePrev = () => {
    setStepIndex(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  const jumpToStep = (index) => {
    setStepIndex(index);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // POST form data directly to Sheets Web App URL
      await axios.post(APPS_SCRIPT_URL, JSON.stringify(formData), {
        headers: { 'Content-Type': 'text/plain' }
      });
      toast.success("Registration submitted successfully!");
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to sync with Google Sheet. Recording locally.");
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── PDF DOWNLOAD ────────────────────────────────────────
  const handleDownloadPDF = () => {
    const element = pdfRef.current;
    if (!element) return;

    import('html2pdf.js').then(html2pdfModule => {
      const html2pdf = html2pdfModule.default;
      html2pdf()
        .set({
          margin: [4, 4, 4, 4],
          filename: `MCC_Registration_${formData.neetRollNumber || 'form'}_${(formData.studentName || 'student').replace(/\s+/g, '_')}.pdf`,
          image: { type: 'jpeg', quality: 0.92 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: 794,
            windowWidth: 794
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        })
        .from(element)
        .save()
        .then(() => {
          // capture done
        })
        .catch(() => {
          // error handling
        });
    });
  };

  // PDF layout helper functions
  const sh = (num, title) => (
    <tr>
      <td colSpan={2} style={{ background: '#0ea5e9', color: '#fff', padding: '7px 12px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
        {num}. {title}
      </td>
    </tr>
  );

  const dr = (label, val, shade) => (
    <tr style={{ background: shade ? '#f9fafb' : '#ffffff' }}>
      <td style={{ padding: '6px 12px', color: '#555', fontSize: '10.5px', width: '42%', borderBottom: '1px solid #f0f0f0' }}>{label}</td>
      <td style={{ padding: '6px 12px', fontSize: '10.5px', fontWeight: '600', color: '#111', borderBottom: '1px solid #f0f0f0' }}>{val || '—'}</td>
    </tr>
  );

  // ─── SUCCESS SCREEN ────────────────────────────────────
  if (submitted) {
    return (
      <div className="cetform-container">
        <Helmet><title>Registration Submitted | Admissions Made Easy</title></Helmet>
        <div className="cetform-success">
          <div className="cetform-success-icon"><i className="fas fa-check-circle"></i></div>
          <h2>MCC Details Submitted Successfully!</h2>
          <p>Your MBBS/BDS MCC details have been recorded. Our team will contact you shortly.</p>
          <div className="cetform-success-details">
            <span><strong>Name:</strong> {formData.studentName}</span>
            <span><strong>NEET Roll Number:</strong> {formData.neetRollNumber}</span>
            <span><strong>Registered Office:</strong> {formData.registeredWith}</span>
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '1.5rem' }}>
            <button className="cetform-btn cetform-btn-pdf" onClick={handleDownloadPDF}>
              <i className="fas fa-file-pdf" style={{ marginRight: 8 }}></i>Download PDF
            </button>
            <button className="cetform-btn cetform-btn-primary" onClick={onBack}>
              <i className="fas fa-arrow-left" style={{ marginRight: 8 }}></i>Back to Dashboard
            </button>
          </div>
        </div>

        {/* HIDDEN TEMPLATE FOR PDF EXPORT */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <div 
            ref={pdfRef} 
            style={{ 
              background: '#ffffff', 
              color: '#111111', 
              fontFamily: 'Arial, Helvetica, sans-serif', 
              padding: '24px', 
              width: '794px', 
              margin: '0 auto',
              boxSizing: 'border-box'
            }}
          >
            {/* ═══ HEADER ═══ */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ fontSize: '21px', fontWeight: '900', color: '#111', lineHeight: '1.1', letterSpacing: '-0.3px' }}>
                      Admissions <span style={{ color: '#0ea5e9' }}>Made Easy</span>
                    </div>
                    <div style={{ fontSize: '8px', color: '#999', letterSpacing: '1.4px', marginTop: '4px', textTransform: 'uppercase' }}>
                      Smart CET-CELL Registration Assistant
                    </div>
                  </td>
                  <td style={{ textAlign: 'right', verticalAlign: 'top' }}>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: '#0ea5e9', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                      MCC Admission Registration Details
                    </div>
                    <div style={{ fontSize: '9px', color: '#555', marginTop: '3px' }}>
                      {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </div>
                    <div style={{ fontSize: '9px', color: '#555', marginTop: '2px' }}>
                      NEET Roll No: <strong>{formData.neetRollNumber || '—'}</strong> &nbsp;|&nbsp; Office: <strong>{formData.registeredWith || '—'}</strong>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* BLUE DIVIDER */}
            <div style={{ height: '2.5px', background: '#0ea5e9', marginBottom: '14px' }}></div>

            {/* ═══ STUDENT SUMMARY CARD ═══ */}
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', marginBottom: '12px' }}>
              <tbody>
                <tr>
                  <td style={{ width: '33%', padding: '12px 14px', verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600' }}>Student Name</div>
                    <div style={{ fontSize: '13px', fontWeight: '800', color: '#111', marginTop: '3px', lineHeight: '1.2' }}>
                      {formData.studentName || '—'}
                    </div>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600', marginTop: '10px' }}>Category</div>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#0ea5e9', marginTop: '3px' }}>{formData.reservationCategory || '—'}</div>
                  </td>
                  <td style={{ width: '33%', padding: '12px 14px', verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600' }}>NEET Roll Number</div>
                    <div style={{ fontSize: '18px', fontWeight: '900', color: '#0ea5e9', marginTop: '3px', lineHeight: '1' }}>{formData.neetRollNumber || '—'}</div>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600', marginTop: '10px' }}>Mobile Number</div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#111', marginTop: '3px' }}>{formData.mobileNumber || '—'}</div>
                  </td>
                  <td style={{ width: '34%', padding: '12px 14px', verticalAlign: 'top' }}>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600' }}>NEET Application Number</div>
                    <div style={{ fontSize: '18px', fontWeight: '900', color: '#111', marginTop: '3px', lineHeight: '1' }}>{formData.neetApplicationNumber || '—'}</div>
                    <div style={{ fontSize: '7.5px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600', marginTop: '10px' }}>Applied For</div>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#111', marginTop: '3px', lineHeight: '1.2' }}>
                      {[...formData.appliedFor, ...(formData.otherAppliedFor ? [formData.otherAppliedFor] : [])].join(', ') || '—'}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ═══ DISCLAIMER BOX ═══ */}
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1.5px solid #fca5a5', background: '#fff5f5', marginBottom: '14px' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontSize: '9px', color: '#dc2626', fontWeight: '700', lineHeight: '1.5' }}>
                      &#9888; DISCLAIMER: This is NOT an official CET-CELL/MCC Registration Form. This document is a Demo Registration Details Form created by Admissions Made Easy for internal reference and guidance purposes only.
                    </div>
                    <div style={{ fontSize: '8.5px', color: '#b91c1c', fontWeight: '500', marginTop: '5px', lineHeight: '1.5' }}>
                      &#9888; सूचना: हा CET-CELL किंवा MCC चा अधिकृत नोंदणी अर्ज नाही. हे दस्तऐवज केवळ Admissions Made Easy द्वारे अंतर्गत संदर्भ आणि मार्गदर्शनासाठी तयार केलेला डेमो नोंदणी तपशील फॉर्म आहे.
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ═══ 2-COLUMN ROWS ═══ */}

            {/* Row 1 */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ width: '49%', verticalAlign: 'top', paddingRight: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(1, 'Registration & Quota')}
                        {dr('Registered Office', formData.registeredWith, false)}
                        {dr('Applied Quotas', formData.appliedFor.join(', ') || 'None', true)}
                        {formData.otherAppliedFor && dr('Other Quotas', formData.otherAppliedFor, false)}
                      </tbody>
                    </table>
                  </td>
                  <td style={{ width: '51%', verticalAlign: 'top', paddingLeft: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(2, 'Personal & Identity')}
                        {dr('Student Full Name', formData.studentName, false)}
                        {dr('Mother\'s Name', formData.mothersName, true)}
                        {dr('Gender', formData.gender, false)}
                        {dr('Date of Birth', formData.dateOfBirth, true)}
                        {dr('Religion', formData.religion, false)}
                        {dr('Reservation Category', formData.reservationCategory, true)}
                        {dr('PwD Status', formData.pwd, false)}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Row 2 */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ width: '49%', verticalAlign: 'top', paddingRight: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(3, 'Address Details')}
                        {dr('Aadhaar Address', formData.address, false)}
                        {dr('Taluka', formData.taluka, true)}
                        {dr('District', formData.district, false)}
                        {dr('State', formData.state, true)}
                        {dr('Pincode', formData.pinCode, false)}
                      </tbody>
                    </table>
                  </td>
                  <td style={{ width: '51%', verticalAlign: 'top', paddingLeft: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px' }}>
                      <tbody>
                        {sh(4, 'Refund Bank Details')}
                        {dr('Account Holder Name', formData.accountHolderName, false)}
                        {dr('Account Number', formData.accountNumber, true)}
                        {dr('IFSC Code', formData.ifscCode, false)}
                        {dr('Bank Name', formData.bankName, true)}
                        {dr('Branch Address', formData.branchAddress, false)}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* HSC Qualification Details */}
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontSize: '10.5px', marginBottom: '10px' }}>
              <tbody>
                {sh(5, 'HSC / 12th Academic Details')}
                <tr>
                  <td style={{ padding: 0, verticalAlign: 'top', width: '50%' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {dr('HSC Passing Board', formData.hscBoard, false)}
                        {dr('HSC Passing Year', formData.hscPassingYear, true)}
                        {dr('Seat / Roll No', formData.hscSeatNumber, false)}
                      </tbody>
                    </table>
                  </td>
                  <td style={{ padding: 0, verticalAlign: 'top', borderLeft: '1px solid #e2e8f0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {dr('College Name & Address', formData.hscCollegeName, false)}
                        {dr('College Pincode', formData.hscCollegePincode, true)}
                      </tbody>
                    </table>
                  </td>
                </tr>
                <tr style={{ background: '#f9fafb' }}>
                  <td colSpan={2} style={{ padding: '7px 12px', fontSize: '10.5px', color: '#444', borderTop: '1px solid #e2e8f0' }}>
                    <strong>Obtained Marks:</strong> &nbsp;
                    Physics: <strong>{formData.hscPhysicsMarks}/100</strong> &nbsp;|&nbsp;
                    Chemistry: <strong>{formData.hscChemistryMarks}/100</strong> &nbsp;|&nbsp;
                    Biology/Biotech: <strong>{formData.hscBiologyMarks}/100</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* FOOTER */}
            <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: '1.5px solid #e2e8f0', marginTop: '6px' }}>
              <tbody>
                <tr>
                  <td style={{ paddingTop: '8px', fontSize: '8px', color: '#aaa', verticalAlign: 'middle' }}>
                    Auto-generated by <strong style={{ color: '#0ea5e9' }}>Admissions Made Easy</strong> &nbsp;|&nbsp; For internal reference only
                  </td>
                  <td style={{ paddingTop: '8px', fontSize: '8px', color: '#aaa', textAlign: 'right', verticalAlign: 'middle' }}>
                    {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ─── WIZARD STEPS RENDERERS ────────────────────────────
  const renderSectionContent = () => {
    switch (currentStep.id) {
      case 'sec1':
        return (
          <div className="cetform-grid">
            <div className="cetform-group full-width">
              <label>Which office you are registered? <span className="text-red">*</span></label>
              <div className="cetform-radio-card-grid">
                {BRANCH_OFFICES.map(b => (
                  <label key={b} className={`cetform-radio-card ${formData.registeredWith === b ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="registeredWith" 
                      value={b} 
                      checked={formData.registeredWith === b}
                      onChange={handleChange}
                    />
                    <span>{b}</span>
                  </label>
                ))}
              </div>
              {errors.registeredWith && <span className="cetform-error">{errors.registeredWith}</span>}
            </div>

            <div className="cetform-group full-width">
              <label>Do you want to apply for? <span className="text-red">*</span></label>
              <div className="cetform-checkbox-card-grid">
                {QUOTA_OPTIONS.map(q => (
                  <label key={q} className={`cetform-checkbox-card ${formData.appliedFor.includes(q) ? 'selected' : ''}`}>
                    <input 
                      type="checkbox" 
                      checked={formData.appliedFor.includes(q)}
                      onChange={() => handleCheckboxChange(q)}
                    />
                    <span>{q}</span>
                  </label>
                ))}
              </div>
              
              <div style={{ marginTop: '0.75rem' }}>
                <label className="sub-label">Other preference (if any):</label>
                <input 
                  type="text" 
                  name="otherAppliedFor" 
                  className="cetform-input" 
                  placeholder="Specify other quotas"
                  value={formData.otherAppliedFor}
                  onChange={handleChange}
                />
              </div>
              {errors.appliedFor && <span className="cetform-error">{errors.appliedFor}</span>}
            </div>

            <div className="cetform-group">
              <label>Candidate's Full Name (As on NEET Marksheet Card) <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="studentName" 
                className={`cetform-input ${errors.studentName ? 'error' : ''}`}
                value={formData.studentName}
                onChange={handleChange}
                placeholder="Full Name"
              />
              {errors.studentName && <span className="cetform-error">{errors.studentName}</span>}
            </div>

            <div className="cetform-group">
              <label>Mother's Name <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="mothersName" 
                className={`cetform-input ${errors.mothersName ? 'error' : ''}`}
                value={formData.mothersName}
                onChange={handleChange}
                placeholder="Mother's First Name"
              />
              {errors.mothersName && <span className="cetform-error">{errors.mothersName}</span>}
            </div>

            <div className="cetform-group">
              <label>NEET Roll Number <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="neetRollNumber" 
                className={`cetform-input ${errors.neetRollNumber ? 'error' : ''}`}
                value={formData.neetRollNumber}
                onChange={handleChange}
                placeholder="10-digit Roll No"
              />
              {errors.neetRollNumber && <span className="cetform-error">{errors.neetRollNumber}</span>}
            </div>

            <div className="cetform-group">
              <label>NEET Application Number <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="neetApplicationNumber" 
                className={`cetform-input ${errors.neetApplicationNumber ? 'error' : ''}`}
                value={formData.neetApplicationNumber}
                onChange={handleChange}
                placeholder="Application No"
              />
              {errors.neetApplicationNumber && <span className="cetform-error">{errors.neetApplicationNumber}</span>}
            </div>

            <div className="cetform-group">
              <label>Date of Birth <span className="text-red">*</span></label>
              <input 
                type="date" 
                name="dateOfBirth" 
                className={`cetform-input ${errors.dateOfBirth ? 'error' : ''}`}
                value={formData.dateOfBirth}
                onChange={handleChange}
              />
              {errors.dateOfBirth && <span className="cetform-error">{errors.dateOfBirth}</span>}
            </div>

            <div className="cetform-group">
              <label>Gender <span className="text-red">*</span></label>
              <div className="cetform-radio-card-grid binary">
                {['Male', 'Female'].map(g => (
                  <label key={g} className={`cetform-radio-card ${formData.gender === g ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="gender" 
                      value={g} 
                      checked={formData.gender === g}
                      onChange={handleChange}
                    />
                    <span>{g}</span>
                  </label>
                ))}
              </div>
              {errors.gender && <span className="cetform-error">{errors.gender}</span>}
            </div>

            <div className="cetform-group">
              <label>Mobile No (नीट फॉर्म भरतेवेळी वापरलेला) <span className="text-red">*</span></label>
              <input 
                type="tel" 
                name="mobileNumber" 
                className={`cetform-input ${errors.mobileNumber ? 'error' : ''}`}
                value={formData.mobileNumber}
                onChange={handleChange}
                placeholder="10-digit Mobile"
              />
              {errors.mobileNumber && <span className="cetform-error">{errors.mobileNumber}</span>}
            </div>

            <div className="cetform-group">
              <label>Alternate Mobile No</label>
              <input 
                type="tel" 
                name="alternateMobile" 
                className={`cetform-input ${errors.alternateMobile ? 'error' : ''}`}
                value={formData.alternateMobile}
                onChange={handleChange}
                placeholder="Alternate Contact"
              />
              {errors.alternateMobile && <span className="cetform-error">{errors.alternateMobile}</span>}
            </div>

            <div className="cetform-group">
              <label>Religion <span className="text-red">*</span></label>
              <select 
                name="religion" 
                className={`cetform-select ${errors.religion ? 'error' : ''}`}
                value={formData.religion}
                onChange={handleChange}
              >
                <option value="">-- Select Religion --</option>
                {RELIGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {errors.religion && <span className="cetform-error">{errors.religion}</span>}
            </div>

            <div className="cetform-group">
              <label>Category <span className="text-red">*</span></label>
              <select 
                name="reservationCategory" 
                className={`cetform-select ${errors.reservationCategory ? 'error' : ''}`}
                value={formData.reservationCategory}
                onChange={handleChange}
              >
                <option value="">-- Select Category --</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.reservationCategory && <span className="cetform-error">{errors.reservationCategory}</span>}
            </div>

            <div className="cetform-group">
              <label>Person with Disability (PwD) <span className="text-red">*</span></label>
              <div className="cetform-radio-card-grid binary">
                {['Yes', 'No'].map(p => (
                  <label key={p} className={`cetform-radio-card ${formData.pwd === p ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="pwd" 
                      value={p} 
                      checked={formData.pwd === p}
                      onChange={handleChange}
                    />
                    <span>{p}</span>
                  </label>
                ))}
              </div>
              {errors.pwd && <span className="cetform-error">{errors.pwd}</span>}
            </div>
          </div>
        );

      case 'sec2':
        return (
          <div className="cetform-grid">
            <div className="cetform-group full-width">
              <label>12th Board Name <span className="text-red">*</span></label>
              <div className="cetform-radio-card-grid">
                {HSC_BOARDS.map(b => (
                  <label key={b} className={`cetform-radio-card ${formData.hscBoard === b ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="hscBoard" 
                      value={b} 
                      checked={formData.hscBoard === b}
                      onChange={handleChange}
                    />
                    <span>{b}</span>
                  </label>
                ))}
              </div>
              {errors.hscBoard && <span className="cetform-error">{errors.hscBoard}</span>}
            </div>

            <div className="cetform-group">
              <label>Year of Passing HSC <span className="text-red">*</span></label>
              <select 
                name="hscPassingYear" 
                className={`cetform-select ${errors.hscPassingYear ? 'error' : ''}`}
                value={formData.hscPassingYear}
                onChange={handleChange}
              >
                <option value="">-- Select Year --</option>
                {HSC_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              {errors.hscPassingYear && <span className="cetform-error">{errors.hscPassingYear}</span>}
            </div>

            <div className="cetform-group">
              <label>12th Seat/Roll No. <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="hscSeatNumber" 
                className={`cetform-input ${errors.hscSeatNumber ? 'error' : ''}`}
                value={formData.hscSeatNumber}
                onChange={e => handleChange({ target: { name: 'hscSeatNumber', value: e.target.value.toUpperCase() } })}
                placeholder="Seat Number"
              />
              {errors.hscSeatNumber && <span className="cetform-error">{errors.hscSeatNumber}</span>}
            </div>

            <div className="cetform-group full-width">
              <label>12th/HSC College Name & Address <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="hscCollegeName" 
                className={`cetform-input ${errors.hscCollegeName ? 'error' : ''}`}
                value={formData.hscCollegeName}
                onChange={handleChange}
                placeholder="School/College Name, City, State"
              />
              {errors.hscCollegeName && <span className="cetform-error">{errors.hscCollegeName}</span>}
            </div>

            <div className="cetform-group">
              <label>12th College Pincode <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="hscCollegePincode" 
                className={`cetform-input ${errors.hscCollegePincode ? 'error' : ''}`}
                value={formData.hscCollegePincode}
                onChange={handleChange}
                placeholder="6-digit Pincode"
              />
              {errors.hscCollegePincode && <span className="cetform-error">{errors.hscCollegePincode}</span>}
            </div>

            <div className="cetform-group">
              <label>Physics Obtained Marks <span className="text-red">*</span></label>
              <input 
                type="number" 
                name="hscPhysicsMarks" 
                className={`cetform-input ${errors.hscPhysicsMarks ? 'error' : ''}`}
                value={formData.hscPhysicsMarks}
                onChange={handleChange}
                placeholder="/100"
                min="0"
                max="100"
              />
              {errors.hscPhysicsMarks && <span className="cetform-error">{errors.hscPhysicsMarks}</span>}
            </div>

            <div className="cetform-group">
              <label>Chemistry Obtained Marks <span className="text-red">*</span></label>
              <input 
                type="number" 
                name="hscChemistryMarks" 
                className={`cetform-input ${errors.hscChemistryMarks ? 'error' : ''}`}
                value={formData.hscChemistryMarks}
                onChange={handleChange}
                placeholder="/100"
                min="0"
                max="100"
              />
              {errors.hscChemistryMarks && <span className="cetform-error">{errors.hscChemistryMarks}</span>}
            </div>

            <div className="cetform-group">
              <label>Biology / Biotechnology Obtained Marks <span className="text-red">*</span></label>
              <input 
                type="number" 
                name="hscBiologyMarks" 
                className={`cetform-input ${errors.hscBiologyMarks ? 'error' : ''}`}
                value={formData.hscBiologyMarks}
                onChange={handleChange}
                placeholder="/100"
                min="0"
                max="100"
              />
              {errors.hscBiologyMarks && <span className="cetform-error">{errors.hscBiologyMarks}</span>}
            </div>
          </div>
        );

      case 'sec3':
        return (
          <div className="cetform-grid">
            <div className="cetform-group full-width">
              <label>Address (as per Aadhaar Card) <span className="text-red">*</span></label>
              <textarea 
                name="address" 
                className={`cetform-textarea ${errors.address ? 'error' : ''}`}
                value={formData.address}
                onChange={handleChange}
                placeholder="Full residential address"
                rows="3"
              />
              {errors.address && <span className="cetform-error">{errors.address}</span>}
            </div>

            <div className="cetform-group">
              <label>Taluka <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="taluka" 
                className={`cetform-input ${errors.taluka ? 'error' : ''}`}
                value={formData.taluka}
                onChange={handleChange}
                placeholder="Taluka"
              />
              {errors.grayDistrict && <span className="cetform-error">{errors.taluka}</span>}
            </div>

            <div className="cetform-group">
              <label>District <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="district" 
                className={`cetform-input ${errors.district ? 'error' : ''}`}
                value={formData.district}
                onChange={handleChange}
                placeholder="District Name"
              />
              {errors.district && <span className="cetform-error">{errors.district}</span>}
            </div>

            <div className="cetform-group">
              <label>State <span className="text-red">*</span></label>
              <select 
                name="state" 
                className={`cetform-select ${errors.state ? 'error' : ''}`}
                value={formData.state}
                onChange={handleChange}
              >
                <option value="">-- Select State --</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {errors.state && <span className="cetform-error">{errors.state}</span>}
            </div>

            <div className="cetform-group">
              <label>Pincode <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="pinCode" 
                className={`cetform-input ${errors.pinCode ? 'error' : ''}`}
                value={formData.pinCode}
                onChange={handleChange}
                placeholder="6-digit Pin Code"
              />
              {errors.pinCode && <span className="cetform-error">{errors.pinCode}</span>}
            </div>
          </div>
        );

      case 'sec4':
        return (
          <div className="cetform-grid">
            <div className="cetform-group">
              <label>Name of Account Holder <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="accountHolderName" 
                className={`cetform-input ${errors.accountHolderName ? 'error' : ''}`}
                value={formData.accountHolderName}
                onChange={handleChange}
                placeholder="Account Holder's Name"
              />
              {errors.accountHolderName && <span className="cetform-error">{errors.accountHolderName}</span>}
            </div>

            <div className="cetform-group">
              <label>Account No. <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="accountNumber" 
                className={`cetform-input ${errors.accountNumber ? 'error' : ''}`}
                value={formData.accountNumber}
                onChange={e => handleChange({ target: { name: 'accountNumber', value: e.target.value.replace(/\D/g, '') } })}
                placeholder="Bank Account Number"
              />
              {errors.accountNumber && <span className="cetform-error">{errors.accountNumber}</span>}
            </div>

            <div className="cetform-group">
              <label>IFSC Code <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="ifscCode" 
                className={`cetform-input ${errors.ifscCode ? 'error' : ''}`}
                value={formData.ifscCode}
                onChange={e => handleChange({ target: { name: 'ifscCode', value: e.target.value.toUpperCase() } })}
                placeholder="11-character IFSC Code"
              />
              {errors.ifscCode && <span className="cetform-error">{errors.ifscCode}</span>}
            </div>

            <div className="cetform-group">
              <label>Bank Name <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="bankName" 
                className={`cetform-input ${errors.bankName ? 'error' : ''}`}
                value={formData.bankName}
                onChange={handleChange}
                placeholder="e.g. State Bank of India"
              />
              {errors.bankName && <span className="cetform-error">{errors.bankName}</span>}
            </div>

            <div className="cetform-group full-width">
              <label>Branch Address <span className="text-red">*</span></label>
              <input 
                type="text" 
                name="branchAddress" 
                className={`cetform-input ${errors.branchAddress ? 'error' : ''}`}
                value={formData.branchAddress}
                onChange={handleChange}
                placeholder="Full bank branch address"
              />
              {errors.branchAddress && <span className="cetform-error">{errors.branchAddress}</span>}
            </div>
          </div>
        );

      case 'sec5':
        return (
          <div className="cetform-review-container">
            <div className="cetform-review-card">
              <div className="cetform-review-header">
                <h3>1. Registration & Quota Preferences</h3>
                <button className="cetform-btn-edit" onClick={() => jumpToStep(0)}>Edit</button>
              </div>
              <div className="cetform-review-body">
                <div className="cetform-review-line"><strong>Branch Office:</strong> <span>{formData.registeredWith}</span></div>
                <div className="cetform-review-line"><strong>Applied Quotas:</strong> <span>{formData.appliedFor.join(', ') || 'None'}</span></div>
                {formData.otherAppliedFor && <div className="cetform-review-line"><strong>Other Quotas:</strong> <span>{formData.otherAppliedFor}</span></div>}
              </div>
            </div>

            <div className="cetform-review-card">
              <div className="cetform-review-header">
                <h3>2. Personal & Identity Details</h3>
                <button className="cetform-btn-edit" onClick={() => jumpToStep(0)}>Edit</button>
              </div>
              <div className="cetform-review-body">
                <div className="cetform-review-line"><strong>Candidate Name:</strong> <span>{formData.studentName}</span></div>
                <div className="cetform-review-line"><strong>Mother's Name:</strong> <span>{formData.mothersName}</span></div>
                <div className="cetform-review-line"><strong>NEET Roll Number:</strong> <span>{formData.neetRollNumber}</span></div>
                <div className="cetform-review-line"><strong>NEET App Number:</strong> <span>{formData.neetApplicationNumber}</span></div>
                <div className="cetform-review-line"><strong>Gender / DOB:</strong> <span>{formData.gender} / {formData.dateOfBirth}</span></div>
                <div className="cetform-review-line"><strong>Mobile / Alternate:</strong> <span>{formData.mobileNumber} / {formData.alternateMobile || '—'}</span></div>
                <div className="cetform-review-line"><strong>Religion / Category:</strong> <span>{formData.religion} / {formData.reservationCategory}</span></div>
                <div className="cetform-review-line"><strong>PwD Status:</strong> <span>{formData.pwd}</span></div>
              </div>
            </div>

            <div className="cetform-review-card">
              <div className="cetform-review-header">
                <h3>3. Qualification Details</h3>
                <button className="cetform-btn-edit" onClick={() => jumpToStep(1)}>Edit</button>
              </div>
              <div className="cetform-review-body">
                <div className="cetform-review-line"><strong>HSC Board / Year:</strong> <span>{formData.hscBoard} / {formData.hscPassingYear}</span></div>
                <div className="cetform-review-line"><strong>Seat / Roll No:</strong> <span>{formData.hscSeatNumber}</span></div>
                <div className="cetform-review-line"><strong>College Name & Address:</strong> <span>{formData.hscCollegeName} (Pin: {formData.hscCollegePincode})</span></div>
                <div className="cetform-review-line"><strong>Physics Marks:</strong> <span>{formData.hscPhysicsMarks}/100</span></div>
                <div className="cetform-review-line"><strong>Chemistry Marks:</strong> <span>{formData.hscChemistryMarks}/100</span></div>
                <div className="cetform-review-line"><strong>Biology Marks:</strong> <span>{formData.hscBiologyMarks}/100</span></div>
              </div>
            </div>

            <div className="cetform-review-card">
              <div className="cetform-review-header">
                <h3>4. Aadhaar Address Details</h3>
                <button className="cetform-btn-edit" onClick={() => jumpToStep(2)}>Edit</button>
              </div>
              <div className="cetform-review-body">
                <div className="cetform-review-line"><strong>Aadhaar Address:</strong> <span>{formData.address}</span></div>
                <div className="cetform-review-line"><strong>Taluka / District:</strong> <span>{formData.taluka} / {formData.district}</span></div>
                <div className="cetform-review-line"><strong>State / Pincode:</strong> <span>{formData.state} — {formData.pinCode}</span></div>
              </div>
            </div>

            <div className="cetform-review-card">
              <div className="cetform-review-header">
                <h3>5. Refund Bank Details</h3>
                <button className="cetform-btn-edit" onClick={() => jumpToStep(3)}>Edit</button>
              </div>
              <div className="cetform-review-body">
                <div className="cetform-review-line"><strong>Account Holder Name:</strong> <span>{formData.accountHolderName}</span></div>
                <div className="cetform-review-line"><strong>Account Number:</strong> <span>{formData.accountNumber}</span></div>
                <div className="cetform-review-line"><strong>IFSC Code / Bank Name:</strong> <span>{formData.ifscCode} — {formData.bankName}</span></div>
                <div className="cetform-review-line"><strong>Branch Address:</strong> <span>{formData.branchAddress}</span></div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isFinalStep = stepIndex === steps.length - 1;
  const progressPercent = ((stepIndex + 1) / steps.length) * 100;

  return (
    <div className="cetform-container">
      <Helmet>
        <title>MBBS/BDS MCC Details Form | Admissions Made Easy</title>
      </Helmet>

      {/* Back to selector dashboard */}
      <button className="cetform-btn-back" onClick={onBack}>
        <i className="fas fa-chevron-left"></i> Back to Dashboard
      </button>

      <div className="cetform-wrapper-inner">
        {/* Header */}
        <div className="cetform-header" style={{ marginBottom: '1.5rem', paddingBottom: '0' }}>
          <h1 style={{ fontSize: '1.75rem' }}>MBBS/BDS MCC Admission Details</h1>
          <p style={{ fontSize: '0.85rem' }}>AIIMS / JIPMER / 15% AIQ / Deemed Universities registration card</p>
        </div>

        {/* Progress indicator */}
        <div className="cetform-progress-wrapper" style={{ marginTop: '0' }}>
          <div className="cetform-progress-bar-container">
            <div className="cetform-progress-bar-fill" style={{ width: `${progressPercent}%` }}></div>
          </div>
          <div className="cetform-active-step-header">
            <span className="cetform-active-step-badge">Section {stepIndex + 1} of {steps.length}</span>
            <span className="cetform-active-step-title">{currentStep.title}</span>
          </div>
        </div>

        {/* Step Content */}
        <div className="cetform-step-content">
          {renderSectionContent()}
        </div>

        {/* Navigation */}
        <div className="cetform-nav">
          {stepIndex > 0 && (
            <button className="cetform-btn cetform-btn-secondary" onClick={handlePrev}>
              <i className="fas fa-arrow-left" style={{ marginRight: 8 }}></i>Previous
            </button>
          )}
          <div className="cetform-nav-spacer"></div>
          {!isFinalStep ? (
            <button className="cetform-btn cetform-btn-primary" onClick={handleNext}>
              Next Section<i className="fas fa-arrow-right" style={{ marginLeft: 8 }}></i>
            </button>
          ) : (
            <button className="cetform-btn cetform-btn-submit" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Submitting...</>
              ) : (
                <><i className="fas fa-paper-plane" style={{ marginRight: 8 }}></i>Submit Details</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
